import type { RenderContext } from '../render/context/createRenderContext';
import type { NodeType } from '../render/context/node';
import { computed, signal } from '../common-utils/signal';
import { ComputedValue, SignalValue } from '../common-utils/signal/reactive-value';

export interface SelectionAPI {
  getSelectedNodes: () => NodeType[];
  selectNode: (ids: string | string[]) => void;
  unselectNode: (ids: string | string[]) => void;
  clearSelect: () => void;
}

export class SelectionProvider {
  selectedIds$: SignalValue<Set<string>> = signal(new Set(), true);
  hoveredIds: Set<string> = new Set();
  stageContext!: RenderContext;
  selectedNodes$: ComputedValue<NodeType[]>;

  onChange: () => void = () => {};

  constructor() {
    this.selectedNodes$ = computed(() => {
      return Array.from(this.selectedIds$.value)
        .map((id) => this.stageContext.api.getNode(id))
        .filter(Boolean) as NodeType[];
    });
  }

  get selectedIds() {
    return this.selectedIds$.value;
  }

  attach(stageContext: RenderContext) {
    this.onChange = stageContext.hooks.onSelectionChangeCallback;
    this.stageContext = stageContext;

    const api = {
      getSelectedNodes: () => {
        return stageContext.api
          .getNodes()
          .filter((node) => stageContext.providers.selection.hasSelect(node.id));
      },
      selectNode: (ids) => {
        stageContext.providers.selection.addSelect(ids);
        stageContext.api.markNeedRender();
      },
      unselectNode: (ids) => {
        stageContext.providers.selection.removeSelect(ids);
        stageContext.api.markNeedRender();
      },
      clearSelect: () => {
        stageContext.providers.selection.clearSelect();
        stageContext.api.markNeedRender();
      },
    };

    Object.assign(this.stageContext.api, api);
  }

  get hovers() {
    return Array.from(this.hoveredIds)
      .map((id) => this.stageContext.api.getNode(id))
      .filter((n) => !!n);
  }

  addHover(id) {
    if (this.hoveredIds.has(id)) return;
    this.clearHover();
    this.hoveredIds.add(id);
  }

  hasHover(id) {
    return this.hoveredIds.has(id);
  }

  hasSelect(id) {
    return this.selectedIds.has(id);
  }

  clearHover() {
    this.hoveredIds.clear();
  }

  addSelect(id: string | string[]) {
    const ids: string[] = Array.isArray(id) ? id : [id];
    const { action } = this.stageContext.providers;

    // Build the next selection set based on shift-key behavior
    const nextSelectedIds = new Set<string>();
    if (action.isShiftKeyPressed) {
      this.selectedIds.forEach((existingId) => nextSelectedIds.add(existingId));
      // Toggle behavior: if the clicked node is already selected, remove it
      ids.forEach((incomingId) => {
        if (nextSelectedIds.has(incomingId)) {
          nextSelectedIds.delete(incomingId);
        } else {
          nextSelectedIds.add(incomingId);
        }
      });
    } else {
      ids.forEach((incomingId) => nextSelectedIds.add(incomingId));
    }

    // If resulting selection is identical, skip updates and onChange
    if (nextSelectedIds.size === this.selectedIds.size) {
      let isSame = true;
      for (const existingId of this.selectedIds) {
        if (!nextSelectedIds.has(existingId)) {
          isSame = false;
          break;
        }
      }
      if (isSame) return;
    }

    // Apply the new selection in one go to avoid redundant onChange
    this.selectedIds.clear();
    nextSelectedIds.forEach((val) => this.selectedIds.add(val));
    this.onChange();
  }

  removeSelect(id) {
    const ids = Array.isArray(id) ? id : [id];
    ids.forEach((id) => {
      this.selectedIds.delete(id);
    });
    this.onChange();
  }

  clearSelect() {
    this.selectedIds.clear();
    this.onChange();
  }

  dispose() {}
}

declare module '../render/context/createRenderContext' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface RendererAPI extends SelectionAPI {}
}
